(() => {
  if (window.tokuTierPricingLoaded) return;
  window.tokuTierPricingLoaded = true;
  const initialized = new WeakSet();
  const watchedSections = new Set();
  const variantMaps = new WeakMap();
  const selections = new WeakMap();
  const pending = new WeakSet();
  const money = (panel, amount) => new Intl.NumberFormat(panel.dataset.locale || 'en', { style:'currency', currency:panel.dataset.currency || 'GBP' }).format(amount / 100);
  const showError = (panel, message) => {
    const error = panel.querySelector('[data-toku-selection-error]');
    error.textContent = message;
    error.hidden = !message;
  };
  const syncItems = (panel, quantity, variantId) => {
    const list = panel.querySelector('[data-toku-variant-list]');
    const total = panel.querySelector('[data-toku-selection-total]');
    const variants = variantMaps.get(panel) || [];
    let state = selections.get(panel);
    if (!state || state.base !== variantId) state = { base:variantId, ids:[], count:0 };
    const count = Number.isInteger(quantity) && quantity <= 250 ? quantity : 0;
    if (count < 2) {
      state.ids = [];
      state.count = 0;
      selections.set(panel, state);
      list.hidden = true;
      total.hidden = true;
      return;
    }
    while (state.ids.length < count) state.ids.push(variantId);
    state.ids.length = count;
    if (state.count !== count || state.renderedBase !== variantId) {
      const fragment = document.createDocumentFragment();
      state.ids.forEach((id, index) => {
        const label = document.createElement('label');
        label.className = 'toku-item-variant';
        const name = document.createElement('span');
        name.textContent = panel.dataset.itemLabel + ' ' + (index + 1);
        const select = document.createElement('select');
        select.dataset.tokuItem = String(index);
        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title + ' · ' + money(panel, Math.round(variant.price * (count >= 3 ? .75 : .8)));
          option.disabled = !variant.available;
          select.append(option);
        });
        select.value = id;
        label.append(name, select);
        fragment.append(label);
      });
      list.replaceChildren(fragment);
      state.count = count;
      state.renderedBase = variantId;
    }
    selections.set(panel, state);
    list.hidden = false;
    const ratio = count >= 3 ? .75 : .8;
    const amounts = new Map();
    state.ids.forEach(id => amounts.set(id, (amounts.get(id) || 0) + 1));
    const discounted = Array.from(amounts, ([id, qty]) => Math.round(Number(variants.find(v => String(v.id) === id)?.price || 0) * qty * ratio)).reduce((a,b) => a+b,0);
    const text = panel.dataset.totalLabel + ': ' + money(panel, discounted);
    if (total.textContent !== text) total.textContent = text;
    total.hidden = false;
  };
  let scheduled = false;
  const quantityInput = form => Array.from(form?.elements || []).find(el => el.name === 'quantity' && el.type === 'number');
  const tierFor = quantity => quantity >= 3 ? 3 : quantity >= 2 ? 2 : 1;
  const sync = panel => {
    if (pending.has(panel)) return;
    const form = document.getElementById(panel.dataset.formId);
    const input = quantityInput(form);
    const quantity = Math.max(1, Number(input?.value) || 1);
    const variantId = form?.querySelector('[name="id"]')?.value;
    const variant = variantMaps.get(panel)?.find(value => String(value.id) === variantId);
    const selectedTier = tierFor(quantity);
    syncItems(panel, quantity, variantId);
    panel.querySelectorAll('[data-toku-tier]').forEach(button => {
      const count = Number(button.dataset.tokuTier);
      const min = Number(variant?.min || input?.min || 1);
      const step = Number(variant?.step || input?.step || 1);
      const max = Number(variant?.max || input?.max || Infinity);
      button.disabled = !input || !variant?.available || count < min || count > max || (count - min) % step !== 0;
      const pressed = String(selectedTier === count);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
    const host = panel.closest('[id^="x-product-template-"]') || panel.closest('.shopify-section');
    host?.querySelectorAll('[data-toku-product-price]').forEach(price => {
      if (price.dataset.productId !== panel.dataset.productId) return;
      price.querySelectorAll('[data-toku-tier-price]').forEach(value => {
        const hidden = Number(value.dataset.tokuTierPrice) !== selectedTier;
        if (value.hidden !== hidden) value.hidden = hidden;
      });
    });
  };
  const scan = () => {
    scheduled = false;
    document.querySelectorAll('[data-toku-tiers]').forEach(panel => {
      if (!initialized.has(panel)) {
        initialized.add(panel);
        try { variantMaps.set(panel, JSON.parse(panel.querySelector('[data-toku-tier-variants]').textContent)); }
        catch { variantMaps.set(panel, []); }
        const section = panel.dataset.sectionId;
        if (!watchedSections.has(section)) {
          watchedSections.add(section);
          document.addEventListener('quantity:changed-' + section, schedule);
          document.addEventListener('eurus:product:quantity-changed-' + section, schedule);
        }
      }
      sync(panel);
    });
  };
  const schedule = () => {
    if (!scheduled) { scheduled = true; requestAnimationFrame(scan); }
  };
  const panelFor = form => Array.from(document.querySelectorAll('[data-toku-tiers]')).find(panel => panel.dataset.formId === form?.getAttribute('id') || 'product-form-sticky-' + panel.dataset.sectionId === form?.getAttribute('id'));
  const addSelection = async (panel, checkout) => {
    if (pending.has(panel)) return;
    sync(panel);
    const form = document.getElementById(panel.dataset.formId);
    const quantity = Number(quantityInput(form)?.value);
    const state = selections.get(panel);
    const variants = variantMaps.get(panel) || [];
    const counts = new Map();
    state?.ids.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
    const invalid = !state || state.ids.length !== quantity || !Number.isInteger(quantity) || Array.from(counts).some(([id, count]) => {
      const variant = variants.find(v => String(v.id) === id);
      const min = Number(variant?.min || 1);
      return !variant?.available || count < min || (variant.max && count > variant.max) || (count - min) % Number(variant.step || 1) !== 0;
    });
    if (invalid) { showError(panel, panel.dataset.errorMessage); return; }
    if (!form.reportValidity()) return;
    pending.add(panel);
    showError(panel, '');
    const controls = Array.from(panel.querySelectorAll('button,select'));
    const host = panel.closest('[id^="x-product-template-"]') || panel.closest('.shopify-section');
    controls.push(...Array.from(host?.querySelectorAll('.add_to_cart_button,[data-toku-buy-now]') || []));
    const previous = controls.map(control => [control, control.disabled]);
    const data = new FormData(form);
    previous.forEach(([control]) => { control.disabled = true; control.setAttribute('aria-busy', 'true'); });
    const properties = {};
    for (const [key,value] of data) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const items = Array.from(counts, ([id, count]) => ({ id, quantity:count, properties, ...(data.get('selling_plan') ? { selling_plan:data.get('selling_plan') } : {}) }));
    try {
      const response = await fetch(panel.dataset.addUrl, { method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify({items}) });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.description || result?.message || panel.dataset.errorMessage);
      if (!result?.items) throw new Error(panel.dataset.networkError);
      window.location.assign(checkout ? '/checkout' : panel.dataset.cartUrl);
    } catch (error) {
      showError(panel, error instanceof TypeError ? panel.dataset.networkError : error.message);
      previous.forEach(([control, disabled]) => { control.disabled = disabled; control.removeAttribute('aria-busy'); });
      pending.delete(panel);
      schedule();
    }
  };
  document.addEventListener('click', event => {
    const button = event.target.closest('.add_to_cart_button,[data-toku-buy-now]');
    if (!button || button.disabled) return;
    const form = button.form || document.getElementById(button.dataset.formId);
    const panel = panelFor(form);
    if (!panel || Number(quantityInput(document.getElementById(panel.dataset.formId))?.value) < 2) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void addSelection(panel, button.hasAttribute('data-toku-buy-now'));
  }, true);
  document.addEventListener('submit', event => {
    const panel = panelFor(event.target);
    if (!panel || Number(quantityInput(document.getElementById(panel.dataset.formId))?.value) < 2) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void addSelection(panel, false);
  }, true);
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-toku-item]');
    if (!select) return;
    const panel = select.closest('[data-toku-tiers]');
    const state = selections.get(panel);
    if (state) state.ids[Number(select.dataset.tokuItem)] = select.value;
    showError(panel, '');
    schedule();
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-toku-tier]');
    if (!button || button.disabled) { schedule(); return; }
    const panel = button.closest('[data-toku-tiers]');
    const form = document.getElementById(panel.dataset.formId);
    const input = quantityInput(form);
    if (!input) return;
    event.preventDefault();
    const quantity = Number(button.dataset.tokuTier);
    input.value = String(quantity);
    input.dispatchEvent(new Event('input', { bubbles:true }));
    input.dispatchEvent(new Event('change', { bubbles:true }));
    input.dispatchEvent(new Event('blur', { bubbles:true }));
    document.dispatchEvent(new CustomEvent('eurus:product:quantity-changed-' + panel.dataset.sectionId, { detail:{ quantity } }));
    schedule();
  });
  document.addEventListener('input', schedule);
  document.addEventListener('change', schedule);
  new MutationObserver(schedule).observe(document.body, { childList:true, subtree:true });
  scan();
})();

